import React from 'react';

import UserFilter from 'components/Filters/UserFilter';

import { Filter, FilterActions } from '@gogame/filters';
import { isGoGameEnv } from 'App/constants';

import {
  BasePeriodFilter,
  BasePayToFilter,
  BaseTrafficTypeFilter,
  BaseLabelsFilter,
} from 'scenes/Trackers/components/Filters';

import {
  FILTER_PERIOD,
  FILTER_PAY_TO,
  FILTER_GENRES,
  FILTER_RE_COUNTER_TYPES,
  FILTER_NETWORKS,
  FILTER_BUSINESS_PARTNERS,
  FILTER_AFFILIATES,
  FILTER_CAMPAIGNS,
  FILTER_MANAGERS,
  FILTER_COUNTRIES,
  FILTER_PLATFORMS,
  FILTER_APPLICATIONS,
  FILTER_CLUSTERS,
  FILTER_OWNERS,
  FILTER_LABELS,
  FILTER_CLIENTS,
  FILTER_TRAFFIC_TYPE,
  FILTER_INCLUDE_ARCHIVED,
  FILTER_CHANNELS,
  FILTER_REGISTRATION_TYPE,
  FILTER_REAL_CP_FLOWS,
  FILTER_REAL_COUNTRIES,
  FILTER_CAMPAIGNS_TYPES,
  FILTER_CHANNEL_TYPE,
  FILTER_STATUSES,
  FILTER_FRAUD_TYPE,
  FILTER_DEVICE_TYPE,
  FILTER_BRIEFS,
  FILTER_INFLUENCER_CHANNEL_SUBSCRIBERS,
  FILTER_INFLUENCER_CHANNEL_TOTAL_VIEWS,
  FILTER_INFLUENCER_CHANNEL_PRICES,
  FILTER_INFLUENCER_CHANNEL_SOCIAL_MEDIA_PLATFORMS,
  FILTER_INFLUENCER_CHANNEL_GENDERS,
  FILTER_CPE_LIVE_EVENTS,
  FILTER_CPE_LIVE_EVENT_TYPE,
} from 'scenes/Trackers/Tracker/constants';

import {
  loadGenres,
  loadReCounters,
  loadNetworks,
  loadBusinessPartners,
  loadAffiliates,
  loadManagers,
  loadCountries,
  loadPlatforms,
  loadApplications,
  loadClusters,
  loadOwners,
  loadLabels,
  loadTrafficTypes,
  loadClients,
  loadChannels,
  loadRegistrations,
  loadRealCPFlows,
  loadCampaignTypes,
  loadChannelTypes,
  loadStatuses,
  loadFraudTypes,
  loadDeviceTypes,
  loadBriefs,
  loadInfluencerChannelSubscribers,
  loadInfluencerChannelTotalViews,
  loadInfluencerChannelSocialMediaPlatforms,
  loadInfluencerChannelPrices,
  loadInfluencerChannelGenders,
  loadCampaigns,
  loadCPELiveEvents,
  loadCPELiveEventTypes,
} from 'scenes/Trackers/Tracker/sources/filters';

import { useGetScreenStateProp } from '../../useGetScreenStateProp';

const DEPENDENCIES = {
  [FILTER_APPLICATIONS]: [FILTER_GENRES],
  [FILTER_NETWORKS]: [FILTER_APPLICATIONS],
  [FILTER_CLUSTERS]: [FILTER_NETWORKS],
  [FILTER_BUSINESS_PARTNERS]: [FILTER_APPLICATIONS, FILTER_NETWORKS],
  [FILTER_AFFILIATES]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_CHANNELS,
    FILTER_CHANNEL_TYPE,
    FILTER_BUSINESS_PARTNERS,
  ],
  [FILTER_CAMPAIGNS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_AFFILIATES,
    FILTER_INCLUDE_ARCHIVED,
  ],
  [FILTER_OWNERS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_AFFILIATES,
    FILTER_COUNTRIES,
    FILTER_MANAGERS,
    FILTER_CHANNELS,
  ],
  [FILTER_MANAGERS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_AFFILIATES,
    FILTER_COUNTRIES,
    FILTER_OWNERS,
    FILTER_CHANNELS,
  ],
  [FILTER_PLATFORMS]: [FILTER_NETWORKS, FILTER_APPLICATIONS],
  [FILTER_CLIENTS]: [FILTER_PLATFORMS],
  [FILTER_CHANNELS]: [
    FILTER_APPLICATIONS,
    FILTER_AFFILIATES,
    FILTER_NETWORKS,
    FILTER_PLATFORMS,
    FILTER_COUNTRIES,
    FILTER_MANAGERS,
    FILTER_OWNERS,
    FILTER_CHANNEL_TYPE,
  ],
  [FILTER_DEVICE_TYPE]: [FILTER_NETWORKS],
  [FILTER_BRIEFS]: [FILTER_APPLICATIONS],
  [FILTER_CPE_LIVE_EVENTS]: [FILTER_APPLICATIONS],
};

const RegistrationPeriodFilter = ({ payToLocked, ...props }) => (
  <BasePeriodFilter
    {...props}
    name={FILTER_PERIOD}
    payToName={FILTER_PAY_TO}
    withPayTo={!payToLocked}
  />
);

const PayToFilter = ({ ...props }) => {
  return <BasePayToFilter name={FILTER_PAY_TO} {...props} />;
};

const TrafficTypeFilter = (props) => (
  <BaseTrafficTypeFilter
    name={FILTER_TRAFFIC_TYPE}
    source={loadTrafficTypes}
    depends={DEPENDENCIES[FILTER_TRAFFIC_TYPE]}
    {...props}
  />
);

const RECounterFilter = (props) => (
  <Filter.MultiSelect
    title={'RE Counter'}
    placeholder={'All'}
    name={FILTER_RE_COUNTER_TYPES}
    source={loadReCounters}
    depends={DEPENDENCIES[FILTER_RE_COUNTER_TYPES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const GenresFilter = (props) => (
  <Filter.MultiSelect
    title={'Genres'}
    placeholder={'All'}
    name={FILTER_GENRES}
    source={loadGenres}
    depends={DEPENDENCIES[FILTER_GENRES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const NetworksFilter = (props) => (
  <Filter.MultiSelect
    title={'Networks'}
    placeholder={'All'}
    name={FILTER_NETWORKS}
    depends={DEPENDENCIES[FILTER_NETWORKS]}
    source={loadNetworks}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);
const BusinessPartnersFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Business Partners'}
      placeholder={'All'}
      name={FILTER_BUSINESS_PARTNERS}
      source={loadBusinessPartners}
      depends={DEPENDENCIES[FILTER_BUSINESS_PARTNERS]}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};
const AffiliatesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Affiliates'}
      placeholder={'All'}
      name={FILTER_AFFILIATES}
      source={loadAffiliates}
      depends={DEPENDENCIES[FILTER_AFFILIATES]}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};

const CampaignsFilter = (props) => {
  return (
    <Filter.MultiSelect
      serverSideSearch={true}
      title={'Campaigns'}
      placeholder={'All'}
      name={FILTER_CAMPAIGNS}
      source={loadCampaigns}
      depends={DEPENDENCIES[FILTER_CAMPAIGNS]}
      dropdownInputPlaceholder={'Search by name and id'}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};

const ManagersFilter = (props) => (
  <Filter.MultiSelect
    title={'Managers'}
    placeholder={'All'}
    name={FILTER_MANAGERS}
    source={loadManagers}
    withAll={false}
    depends={DEPENDENCIES[FILTER_MANAGERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CountriesFilter = (props) => (
  <Filter.MultiSelect
    title={'Countries'}
    placeholder={'All'}
    name={FILTER_COUNTRIES}
    source={loadCountries}
    depends={DEPENDENCIES[FILTER_COUNTRIES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ChannelsFilter = (props) => (
  <Filter.MultiSelect
    title={'Channels'}
    placeholder={'All'}
    name={FILTER_CHANNELS}
    source={loadChannels}
    depends={DEPENDENCIES[FILTER_CHANNELS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const PlatformsFilter = (props) => (
  <Filter.MultiSelect
    title={'Platforms'}
    placeholder={'All'}
    name={FILTER_PLATFORMS}
    depends={DEPENDENCIES[FILTER_PLATFORMS]}
    source={loadPlatforms}
    withAll={true}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ApplicationsFilter = (props) => (
  <Filter.MultiSelect
    title={'Applications'}
    placeholder={'All'}
    name={FILTER_APPLICATIONS}
    depends={DEPENDENCIES[FILTER_APPLICATIONS]}
    source={loadApplications}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ClustersFilter = (props) => (
  <Filter.MultiSelect
    title={'Clusters'}
    placeholder={'All'}
    name={FILTER_CLUSTERS}
    depends={DEPENDENCIES[FILTER_CLUSTERS]}
    source={loadClusters}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const OwnersFilter = (props) => (
  <UserFilter
    title={'Owners'}
    placeholder={'All'}
    name={FILTER_OWNERS}
    source={loadOwners}
    withAll={false}
    depends={DEPENDENCIES[FILTER_OWNERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const LabelsFilter = (props) => (
  <BaseLabelsFilter
    name={FILTER_LABELS}
    source={loadLabels}
    depends={DEPENDENCIES[FILTER_LABELS]}
    {...props}
  />
);

const ClientsFilter = (props) => (
  <Filter.MultiSelect
    title={'Clients'}
    placeholder={'All'}
    name={FILTER_CLIENTS}
    source={loadClients}
    withAll={false}
    depends={DEPENDENCIES[FILTER_CLIENTS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CPFlowFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_REAL_CP_FLOWS}
    title={'CP flow'}
    placeholder={'All'}
    source={loadRealCPFlows}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);
const RealCountriesFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_REAL_COUNTRIES}
    title={'Real countries'}
    placeholder={'All'}
    source={loadCountries}
    depends={DEPENDENCIES[FILTER_REAL_COUNTRIES]}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const IncludeArchivedFilter = (props) => (
  <Filter.Checkbox
    title={'Include archived'}
    name={FILTER_INCLUDE_ARCHIVED}
    multiFields={false}
    {...props}
    padding={0}
  />
);

const RegistrationsFilter = (props) => (
  <Filter.Select
    name={FILTER_REGISTRATION_TYPE}
    title={'Registrations'}
    source={loadRegistrations}
    {...props}
  />
);

const CampaignTypeFilter = (props) => (
  <Filter.MultiSelect
    title={'Campaign Type'}
    placeholder={'All'}
    name={FILTER_CAMPAIGNS_TYPES}
    source={loadCampaignTypes}
    depends={DEPENDENCIES[FILTER_CAMPAIGNS_TYPES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ChannelTypesFilter = (props) => (
  <Filter.MultiSelect
    title={'Channel type'}
    name={FILTER_CHANNEL_TYPE}
    placeholder={'All'}
    source={loadChannelTypes}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

export const StatusesFilter = (props) => {
  const drillPath = useGetScreenStateProp('drillPath');
  const breakdownPath = useGetScreenStateProp('breakdownPath');
  const partnerDimensions = [
    'Publisher',
    'PartnerAd',
    'Placement',
    'AdSet',
    'RootAdSet',
    'PartnerCampaign',
    'RootPartnerCampaign',
  ];

  const currentDimensions = [...drillPath].pop()?.type;
  const isPartnerDimension = partnerDimensions.includes(currentDimensions);
  const hasPartnerDimensionInBreakdown = breakdownPath.some(({ breakdownId }) =>
    partnerDimensions.includes(breakdownId)
  );
  const disabled = !isPartnerDimension && !hasPartnerDimensionInBreakdown;

  return (
    <Filter.MultiSelect
      title={'Statuses (by partner entities)'}
      name={FILTER_STATUSES}
      placeholder={'All'}
      source={loadStatuses}
      disabled={disabled}
      compareActions={[<FilterActions.Equalize />]}
      renderValue={function renderStatusesValue(selectedOptions) {
        if (disabled) {
          return 'All';
        }
        return selectedOptions.map(({ name }) => name).join(', ');
      }}
      {...props}
    />
  );
};

const FraudTypeFilter = (props) => (
  <Filter.Select
    name={FILTER_FRAUD_TYPE}
    title={'Fraud'}
    source={loadFraudTypes}
    placeholder={'Select fraud type'}
    {...props}
  />
);

const DeviceTypeFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_DEVICE_TYPE}
    title={'Real Device type'}
    source={loadDeviceTypes}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_DEVICE_TYPE]}
    {...props}
  />
);

const InfluencerChannelBriefsFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_BRIEFS}
    title={'Briefs'}
    source={loadBriefs}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_BRIEFS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const InfluencerChannelSubscribersFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_INFLUENCER_CHANNEL_SUBSCRIBERS}
    title={'Subscribers'}
    source={loadInfluencerChannelSubscribers}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_INFLUENCER_CHANNEL_SUBSCRIBERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const InfluencerChannelTotalViewsFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_INFLUENCER_CHANNEL_TOTAL_VIEWS}
    title={'Total Views'}
    source={loadInfluencerChannelTotalViews}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_INFLUENCER_CHANNEL_TOTAL_VIEWS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const InfluencerChannelPricesFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_INFLUENCER_CHANNEL_PRICES}
    title={'Prices'}
    source={loadInfluencerChannelPrices}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_INFLUENCER_CHANNEL_PRICES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const InfluencerChannelSocialMediaPlatformsFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_INFLUENCER_CHANNEL_SOCIAL_MEDIA_PLATFORMS}
    title={'Social Media Platforms'}
    source={loadInfluencerChannelSocialMediaPlatforms}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_INFLUENCER_CHANNEL_SOCIAL_MEDIA_PLATFORMS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const InfluencerChannelGendersFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_INFLUENCER_CHANNEL_GENDERS}
    title={'Genders'}
    source={loadInfluencerChannelGenders}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_INFLUENCER_CHANNEL_GENDERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CPELiveEventsFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_CPE_LIVE_EVENTS}
    title={'CPE Live Events'}
    source={loadCPELiveEvents}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_CPE_LIVE_EVENTS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CPELiveEventTypeFilter = (props) => (
  <Filter.Select
    name={FILTER_CPE_LIVE_EVENT_TYPE}
    title={'Event Display Mode'}
    source={loadCPELiveEventTypes}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_CPE_LIVE_EVENT_TYPE]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

export const InfluencerChannelFilters = isGoGameEnv
  ? {
      Briefs: InfluencerChannelBriefsFilter,
      Subscribers: InfluencerChannelSubscribersFilter,
      TotalViews: InfluencerChannelTotalViewsFilter,
      Prices: InfluencerChannelPricesFilter,
      SocialMediaPlatforms: InfluencerChannelSocialMediaPlatformsFilter,
      Genders: InfluencerChannelGendersFilter,
    }
  : {};

export default {
  RegistrationPeriod: RegistrationPeriodFilter,
  PayTo: PayToFilter,
  Genres: isGoGameEnv ? GenresFilter : null,
  Networks: NetworksFilter,
  BusinessPartners: BusinessPartnersFilter,
  Affiliates: AffiliatesFilter,
  Campaigns: CampaignsFilter,
  Managers: isGoGameEnv ? ManagersFilter : null,
  Countries: CountriesFilter,
  Channels: isGoGameEnv ? ChannelsFilter : null,
  Platforms: PlatformsFilter,
  Applications: ApplicationsFilter,
  Clusters: ClustersFilter,
  Owners: isGoGameEnv ? OwnersFilter : null,
  Labels: isGoGameEnv ? LabelsFilter : null,
  TrafficType: isGoGameEnv ? TrafficTypeFilter : null,
  IncludeArchived: IncludeArchivedFilter,
  CPFlow: CPFlowFilter,
  Clients: isGoGameEnv ? ClientsFilter : null,
  Registrations: RegistrationsFilter,
  RealCountries: RealCountriesFilter,
  CampaignType: CampaignTypeFilter,
  ChannelTypes: isGoGameEnv ? ChannelTypesFilter : null,
  Statuses: isGoGameEnv ? StatusesFilter : null,
  RECounter: isGoGameEnv ? RECounterFilter : null,
  FraudType: FraudTypeFilter,
  DeviceType: isGoGameEnv ? DeviceTypeFilter : null,
  CPELiveEvents: isGoGameEnv ? CPELiveEventsFilter : null,
  CPELiveEventType: isGoGameEnv ? CPELiveEventTypeFilter : null,
};
